import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CountExtendedBookingsOptions,
  CountExtendedBookingsResponse,
  ExtendedBookingQuery,
  ExtendedBookingsQueryBuilder,
  QueryExtendedBookingResponse,
  QueryExtendedBookingsOptions,
  QueryExtendedBookingsResponse,
  QueryOptions,
  QueryV2,
  countExtendedBookings as universalCountExtendedBookings,
  query as universalQuery,
  queryExtendedBookings as universalQueryExtendedBookings,
  typedQueryExtendedBookings as universalTypedQueryExtendedBookings,
} from './bookings-reader-v2-extended-booking-extended-bookings.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/bookings' };

export function query(httpClient: HttpClient): QuerySignature {
  return (query: QueryV2, options?: QueryOptions) =>
    universalQuery(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QuerySignature {
  /**
   * > **Deprecation Notice**
   * >
   * > **This endpoint has been replaced with [Query Extended Bookings](https://dev.wix.com/api/rest/wix-bookings/bookings-reader-v2/query-extended-bookings) and will be removed on May 31, 2025.**
   * > **If your app uses this endpoint, we recommend updating your code as soon as possible.**
   *
   *
   * Retrieves a list of bookings, given the provided paging, filtering, and sorting.
   *
   *
   * You can also retrieve information about which actions the customer can perform
   * for the bookings. To do so, pass `withBookingAllowedActions` as `true`.
   *
   * Query Bookings runs with these defaults:
   *
   * - `createdDate` sorted in `DESC` order
   * - `cursorPaging.limit` is `50`
   *
   * `query.fields` and `query.fieldsets` aren't supported for this endpoint.
   *
   * When using filters for dates, you must use [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
   *
   * Bookings belonging to a schedule don't have a `sessionId`. Therefore you
   * must use the `sessionId` filter that isn't part of the `query` object to
   * filter bookings for courses.
   *
   * To learn about working with _Query_ endpoints, see
   * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
   * [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging),
   * and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
   *
   * When calling Query Bookings, the retrieved data may not contain your most recent changes. See
   * [Wix-data and Eventual Consistency](https://dev.wix.com/docs/api-reference/business-solutions/cms/eventual-consistency)
   * for more information.
   * @param - Information about filters, paging, and sorting.
   * @param - Additional options for performing the query.
   * @deprecated
   */
  (query: QueryV2, options?: QueryOptions): Promise<
    NonNullablePaths<
      QueryExtendedBookingResponse,
      | `extendedBookings`
      | `extendedBookings.${number}.booking.totalParticipants`
      | `extendedBookings.${number}.booking.status`
      | `extendedBookings.${number}.booking.paymentStatus`
      | `extendedBookings.${number}.booking.selectedPaymentOption`
      | `extendedBookings.${number}.booking.createdBy.anonymousVisitorId`
      | `extendedBookings.${number}.booking.createdBy.memberId`
      | `extendedBookings.${number}.booking.createdBy.wixUserId`
      | `extendedBookings.${number}.booking.createdBy.appId`
      | `extendedBookings.${number}.booking.multiServiceBookingInfo.type`
      | `extendedBookings.${number}.allowedActions.cancel`
      | `extendedBookings.${number}.allowedActions.reschedule`
      | `extendedBookings.${number}.attendance.status`
      | `extendedBookings.${number}.attendance.numberOfAttendees`
      | `extendedBookings.${number}.bookingFeeDetails.cancellationFeeStatus`
      | `extendedBookings.${number}.order.number`
      | `extendedBookings.${number}.order.status`
      | `extendedBookings.${number}.order.paymentStatus`
      | `extendedBookings.${number}.order.totalPrice.amount`
      | `extendedBookings.${number}.order.totalPrice.formattedAmount`
      | `extendedBookings.${number}.order.bookingPriceDescription.original`
      | `extendedBookings.${number}.order.balance.amount`
      | `extendedBookings.${number}.order.balance.formattedAmount`
      | `extendedBookings.${number}.waiver.status`
      | `extendedBookings.${number}.subscriptionInfo.currentCycle`
      | `extendedBookings.${number}.subscriptionInfo.totalCycles`
      | `extendedBookings.${number}.subscriptionInfo.billingFrequency`
      | `extendedBookings.${number}.subscriptionInfo.status`
      | `extendedBookings.${number}.subscriptionInfo.cyclePrice.amount`
      | `extendedBookings.${number}.subscriptionInfo.cyclePrice.currency`,
      4
    >
  >;
}

export function queryExtendedBookings(
  httpClient: HttpClient
): QueryExtendedBookingsSignature {
  return (options?: QueryExtendedBookingsOptions) =>
    universalQueryExtendedBookings(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryExtendedBookingsSignature {
  /**
   * Retrieves a list of bookings, including additional extended information, given the provided paging, filtering, and sorting.
   *
   * `queryExtendedBookings()` doesn't use a query builder, instead it uses [API query language](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language#with-the-api-query-language).
   *
   * Up to 100 extended bookings can be returned per request.
   *
   * `queryExtendedBookings()` runs with these defaults, which you can override:
   *
   * - `createdDate` sorted in `DESC` order
   * - `cursorPaging.limit` is `50`
   *
   *
   * You can retrieve information about which actions the customer can perform
   * for the bookings. To do so, pass `withBookingAllowedActions` as `true`.
   *
   *
   * For field support, see
   * [supported filters](https://www.wix.com/velo/reference/wix-bookings-v2/extendedbookings/supported-filters)
   * for more information.
   *
   * You can specify a filter only once per query. If you specify a filter
   * more than once, only the first filter determines the extended bookings that are returned.
   *
   * When filtering by date, you must use [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
   * @param - Additional options for performing the query.
   */
  (options?: QueryExtendedBookingsOptions): ExtendedBookingsQueryBuilder;
}

export function typedQueryExtendedBookings(
  httpClient: HttpClient
): TypedQueryExtendedBookingsSignature {
  return (
    query: ExtendedBookingQuery,
    options?: QueryExtendedBookingsOptions
  ) =>
    universalTypedQueryExtendedBookings(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryExtendedBookingsSignature {
  /** */
  (
    query: ExtendedBookingQuery,
    options?: QueryExtendedBookingsOptions
  ): Promise<
    NonNullablePaths<
      QueryExtendedBookingsResponse,
      | `extendedBookings`
      | `extendedBookings.${number}.booking.totalParticipants`
      | `extendedBookings.${number}.booking.status`
      | `extendedBookings.${number}.booking.paymentStatus`
      | `extendedBookings.${number}.booking.selectedPaymentOption`
      | `extendedBookings.${number}.booking.createdBy.anonymousVisitorId`
      | `extendedBookings.${number}.booking.createdBy.memberId`
      | `extendedBookings.${number}.booking.createdBy.wixUserId`
      | `extendedBookings.${number}.booking.createdBy.appId`
      | `extendedBookings.${number}.booking.multiServiceBookingInfo.type`
      | `extendedBookings.${number}.allowedActions.cancel`
      | `extendedBookings.${number}.allowedActions.reschedule`
      | `extendedBookings.${number}.attendance.status`
      | `extendedBookings.${number}.attendance.numberOfAttendees`
      | `extendedBookings.${number}.bookingFeeDetails.cancellationFeeStatus`
      | `extendedBookings.${number}.order.number`
      | `extendedBookings.${number}.order.status`
      | `extendedBookings.${number}.order.paymentStatus`
      | `extendedBookings.${number}.order.totalPrice.amount`
      | `extendedBookings.${number}.order.totalPrice.formattedAmount`
      | `extendedBookings.${number}.order.bookingPriceDescription.original`
      | `extendedBookings.${number}.order.balance.amount`
      | `extendedBookings.${number}.order.balance.formattedAmount`
      | `extendedBookings.${number}.waiver.status`
      | `extendedBookings.${number}.subscriptionInfo.currentCycle`
      | `extendedBookings.${number}.subscriptionInfo.totalCycles`
      | `extendedBookings.${number}.subscriptionInfo.billingFrequency`
      | `extendedBookings.${number}.subscriptionInfo.status`
      | `extendedBookings.${number}.subscriptionInfo.cyclePrice.amount`
      | `extendedBookings.${number}.subscriptionInfo.cyclePrice.currency`,
      4
    >
  >;
}

export function countExtendedBookings(
  httpClient: HttpClient
): CountExtendedBookingsSignature {
  return (options?: CountExtendedBookingsOptions) =>
    universalCountExtendedBookings(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountExtendedBookingsSignature {
  /**
   * Counts the number of bookings matching the specified filters.
   *
   *
   * See [Supported Filters](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-reader-v2/supported-filters?apiView=SDK) for a list of supported filters.
   */
  (options?: CountExtendedBookingsOptions): Promise<
    NonNullablePaths<CountExtendedBookingsResponse, `count`, 0>
  >;
}

export {
  Actor,
  AdditionalFee,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  Aggregation,
  AggregationData,
  AggregationKindOneOf,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationResultsScalarDateResult,
  AggregationResultsScalarResult,
  AggregationType,
  AllowedActions,
  Attendance,
  AttendanceStatus,
  Balance,
  BookedAddOn,
  BookedEntity,
  BookedEntityItemOneOf,
  BookedResource,
  BookedSchedule,
  BookedSlot,
  Booking,
  BookingFeeDetails,
  BookingFeeStatus,
  BookingParticipantsInfoOneOf,
  BookingPolicySettings,
  BookingSource,
  BookingStatus,
  CommonAddressLocation,
  CommonAggregationType,
  CommonPaging,
  CommonQueryV2,
  CommonQueryV2PagingMethodOneOf,
  CommonScalarType,
  ConferencingDetails,
  ContactDetails,
  CountExtendedBookingsOptions,
  CountExtendedBookingsRequest,
  CountExtendedBookingsResponse,
  CouponInfo,
  CursorPaging,
  CursorPagingMetadata,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  CustomFormField,
  CyclePrice,
  DateHistogramAggregation,
  DateHistogramResult,
  DateHistogramResults,
  DateTimeRule,
  DeferredChargeDeclineReason,
  DeferredChargeInfo,
  DeferredChargeInfoStatusDetailsOneOf,
  DeferredChargeStatus,
  Duration,
  DurationUnitType,
  ExtendedBooking,
  ExtendedBookingQuerySpec,
  ExtendedBookingsQueryBuilder,
  ExtendedBookingsQueryResult,
  ExtendedFields,
  FlowControlSettings,
  FormSubmission,
  Frequency,
  GeotileGridBucket,
  GeotileGridResults,
  GroupByAggregation,
  GroupByAggregationKindOneOf,
  GroupByValueResults,
  IdentificationData,
  IdentificationDataIdOneOf,
  IdentityType,
  IncludeMissingValuesOptions,
  Interval,
  Location,
  LocationType,
  MembershipName,
  MembershipPaymentCredits,
  MembershipPaymentDetails,
  MissingValues,
  Mode,
  MultiServiceBookingInfo,
  MultiServiceBookingType,
  NestedAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  NestedAggregationType,
  NestedResultValue,
  NestedResultValueResultOneOf,
  NestedResults,
  NestedValueAggregationResult,
  Order,
  OrderStatus,
  OrderTransactions,
  Paging,
  PagingMetadataV2,
  ParticipantChoices,
  ParticipantNotification,
  ParticipantsRange,
  PaymentStatus,
  PaymentStatusEnumPaymentStatus,
  PhoneCall,
  Platform,
  Price,
  PriceDescription,
  QueryExtendedBookingRequest,
  QueryExtendedBookingResponse,
  QueryExtendedBookingsOptions,
  QueryExtendedBookingsRequest,
  QueryExtendedBookingsResponse,
  QueryOptions,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RangeAggregation,
  RangeAggregationResult,
  RangeBucket,
  RangeResult,
  RangeResults,
  RequestedGender,
  RequestedGenderValue,
  ResourceSelection,
  ResourceTransparency,
  Results,
  ScalarAggregation,
  ScalarDateResult,
  ScalarResult,
  ScalarType,
  SearchDetails,
  SearchExtendedBookingsRequest,
  SearchExtendedBookingsResponse,
  SelectedPaymentOption,
  SelectionMethod,
  ServiceChoice,
  ServiceChoiceChoiceOneOf,
  ServiceChoices,
  SortDirection,
  SortOrder,
  SortType,
  Sorting,
  Status,
  StreetAddress,
  Subdivision,
  SubscriptionInfo,
  SubscriptionStatus,
  ValueAggregation,
  ValueAggregationOptionsOneOf,
  ValueAggregationResult,
  ValueResult,
  ValueResults,
  ValueType,
  Waiver,
  ZonedDate,
  utils,
} from './bookings-reader-v2-extended-booking-extended-bookings.universal.js';
